import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Alert,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons, Feather } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';

// Import Slider from the correct package
// If you're using React Native 0.60+, use:
import Slider from '@react-native-community/slider';
// OR if you're using Expo:
// import Slider from '@react-native-community/slider';

const FontSizeSettings = () => {
  const navigation = useNavigation();
  const [fontSize, setFontSize] = useState(16); // Default medium size
  const [previewText, setPreviewText] = useState('');
  const [lineHeight, setLineHeight] = useState(1.5);
  const [isBold, setIsBold] = useState(false);

  const colors = {
    primary: '#4F46E5',
    secondary: '#10B981',
    danger: '#EF4444',
    dark: '#1F2937',
    light: '#F9FAFB',
    white: '#FFFFFF',
    gray: '#6B7280',
    lightGray: '#E5E7EB',
    border: '#E5E7EB',
  };

  const fontSizePresets = [
    {
      id: 'extra-small',
      name: 'Extra Small',
      size: 12,
      description: 'Compact text',
      icon: 'type',
    },
    {
      id: 'small',
      name: 'Small',
      size: 14,
      description: 'Standard small',
      icon: 'type',
    },
    {
      id: 'medium',
      name: 'Medium',
      size: 16,
      description: 'Recommended',
      icon: 'type',
      recommended: true,
    },
    {
      id: 'large',
      name: 'Large',
      size: 18,
      description: 'Enhanced readability',
      icon: 'type',
    },
    {
      id: 'extra-large',
      name: 'Extra Large',
      size: 20,
      description: 'High visibility',
      icon: 'type',
    },
    {
      id: 'extra-extra-large',
      name: 'XX Large',
      size: 24,
      description: 'Maximum size',
      icon: 'type',
    },
  ];

  const previewExamples = [
    "The quick brown fox jumps over the lazy dog.",
    "Learning opens new doors to opportunities.",
    "Practice makes perfect. Keep studying!",
    "Knowledge is power. Stay curious.",
    "Every expert was once a beginner."
  ];

  useEffect(() => {
    loadFontSettings();
    // Set random preview text
    setPreviewText(previewExamples[Math.floor(Math.random() * previewExamples.length)]);
    
    // Cleanup function
    return () => {
      // No AppState listener to clean up
    };
  }, []);

  useEffect(() => {
    // Update line height based on font size for better readability
    const newLineHeight = fontSize >= 20 ? 1.4 : fontSize >= 18 ? 1.45 : 1.5;
    setLineHeight(newLineHeight);
  }, [fontSize]);

  const loadFontSettings = async () => {
    try {
      const savedSettings = await AsyncStorage.getItem('font_settings');
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        setFontSize(settings.fontSize || 16);
        setIsBold(settings.isBold || false);
      }
    } catch (error) {
      console.error('Error loading font settings:', error);
    }
  };

  const saveFontSettings = async (newFontSize, bold = isBold) => {
    try {
      const settings = {
        fontSize: newFontSize,
        isBold: bold,
        lastUpdated: new Date().toISOString(),
      };
      await AsyncStorage.setItem('font_settings', JSON.stringify(settings));
      
      // Update state
      setFontSize(newFontSize);
      setIsBold(bold);
      
      // Show success message
      Alert.alert(
        'Font Size Updated',
        'The text size has been updated across the app.',
        [{ text: 'OK' }]
      );
    } catch (error) {
      console.error('Error saving font settings:', error);
      Alert.alert('Error', 'Failed to save font settings');
    }
  };

  const handleFontSizeChange = (value) => {
    setFontSize(Math.round(value));
  };

  const handleFontSizeChangeComplete = (value) => {
    const roundedValue = Math.round(value);
    saveFontSettings(roundedValue);
  };

  const handlePresetSelect = (preset) => {
    saveFontSettings(preset.size);
  };

  const handleBoldToggle = () => {
    const newBoldState = !isBold;
    setIsBold(newBoldState);
    saveFontSettings(fontSize, newBoldState);
  };

  const handleReset = () => {
    Alert.alert(
      'Reset to Default',
      'Reset all font settings to default values?',
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Reset', 
          style: 'destructive',
          onPress: () => saveFontSettings(16, false)
        },
      ]
    );
  };

  const getFontWeight = () => {
    return isBold ? '600' : '400';
  };

  const getAccessibilityLabel = () => {
    if (fontSize <= 12) return 'Extra Small';
    if (fontSize <= 14) return 'Small';
    if (fontSize <= 16) return 'Medium';
    if (fontSize <= 18) return 'Large';
    if (fontSize <= 20) return 'Extra Large';
    return 'XX Large';
  };

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="dark-content" backgroundColor={colors.white} />
      
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerTop}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <Ionicons name="arrow-back" size={24} color={colors.dark} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Font Size</Text>
          <TouchableOpacity onPress={handleReset} style={styles.resetButton}>
            <Text style={styles.resetButtonText}>Reset</Text>
          </TouchableOpacity>
        </View>
        <Text style={styles.headerSubtitle}>
          Adjust text size for better readability
        </Text>
      </View>

      <ScrollView 
        showsVerticalScrollIndicator={false} 
        style={styles.scrollView}
        contentContainerStyle={styles.scrollViewContent}
      >
        {/* Current Size Indicator */}
        <View style={styles.currentSizeSection}>
          <View style={styles.currentSizeCard}>
            <View style={styles.currentSizeHeader}>
              <Feather name="type" size={20} color={colors.primary} />
              <Text style={styles.currentSizeLabel}>Current Size</Text>
            </View>
            <View style={styles.currentSizeValue}>
              <Text style={styles.currentSizeNumber}>{fontSize}px</Text>
              <View style={styles.currentSizeBadge}>
                <Text style={styles.currentSizeBadgeText}>{getAccessibilityLabel()}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Slider Control */}
        <View style={styles.sliderSection}>
          <View style={styles.sliderHeader}>
            <Text style={styles.sectionTitle}>Adjust Size</Text>
            <Text style={styles.sliderValue}>{fontSize}px</Text>
          </View>
          
          <Slider
            style={styles.slider}
            minimumValue={10}
            maximumValue={28}
            step={1}
            value={fontSize}
            onValueChange={handleFontSizeChange}
            onSlidingComplete={handleFontSizeChangeComplete}
            minimumTrackTintColor={colors.primary}
            maximumTrackTintColor={colors.lightGray}
            thumbTintColor={colors.primary}
          />
          
          <View style={styles.sliderLabels}>
            <Text style={[styles.sliderLabel, { fontSize: 10 }]}>A</Text>
            <Text style={[styles.sliderLabel, { fontSize: 14 }]}>A</Text>
            <Text style={[styles.sliderLabel, { fontSize: 18 }]}>A</Text>
            <Text style={[styles.sliderLabel, { fontSize: 22 }]}>A</Text>
            <Text style={[styles.sliderLabel, { fontSize: 26 }]}>A</Text>
          </View>
          
          <View style={styles.sliderSizeLabels}>
            <Text style={styles.sliderSizeLabel}>10px</Text>
            <Text style={styles.sliderSizeLabel}>16px</Text>
            <Text style={styles.sliderSizeLabel}>22px</Text>
            <Text style={styles.sliderSizeLabel}>28px</Text>
          </View>
        </View>

        {/* Quick Presets */}
        <View style={styles.presetsSection}>
          <Text style={styles.sectionTitle}>Quick Presets</Text>
          <View style={styles.presetsGrid}>
            {fontSizePresets.map((preset) => {
              const isActive = fontSize === preset.size;
              return (
                <TouchableOpacity
                  key={preset.id}
                  style={[
                    styles.presetCard,
                    isActive && styles.presetCardActive,
                  ]}
                  onPress={() => handlePresetSelect(preset)}
                >
                  <View style={styles.presetIcon}>
                    <Feather 
                      name={preset.icon} 
                      size={preset.size} 
                      color={isActive ? colors.primary : colors.gray} 
                    />
                  </View>
                  <Text style={[
                    styles.presetName,
                    isActive && styles.presetNameActive,
                    { fontSize: 14 }
                  ]}>
                    {preset.name}
                  </Text>
                  <Text style={styles.presetDescription}>
                    {preset.description}
                  </Text>
                  {preset.recommended && (
                    <View style={styles.recommendedBadge}>
                      <Text style={styles.recommendedBadgeText}>Recommended</Text>
                    </View>
                  )}
                  {isActive && (
                    <View style={styles.activeIndicator}>
                      <Feather name="check-circle" size={16} color={colors.primary} />
                    </View>
                  )}
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        {/* Bold Text Option */}
        <View style={styles.boldSection}>
          <View style={styles.boldCard}>
            <View style={styles.boldLeft}>
              <View style={styles.boldIcon}>
                <Feather name="bold" size={20} color={isBold ? colors.primary : colors.gray} />
              </View>
              <View style={styles.boldText}>
                <Text style={styles.boldTitle}>Bold Text</Text>
                <Text style={styles.boldDescription}>
                  Increase text weight for better contrast
                </Text>
              </View>
            </View>
            <TouchableOpacity
              style={[
                styles.boldToggle,
                isBold && styles.boldToggleActive,
              ]}
              onPress={handleBoldToggle}
            >
              <View style={[
                styles.boldToggleCircle,
                isBold && styles.boldToggleCircleActive,
              ]} />
            </TouchableOpacity>
          </View>
        </View>

        {/* Preview Section */}
        <View style={styles.previewSection}>
          <Text style={styles.sectionTitle}>Preview</Text>
          <View style={styles.previewCard}>
            <Text 
              style={[
                styles.previewText,
                { 
                  fontSize: fontSize,
                  lineHeight: fontSize * lineHeight,
                  fontWeight: getFontWeight(),
                }
              ]}
              numberOfLines={4}
            >
              {previewText}
            </Text>
            <TouchableOpacity
              style={styles.refreshPreview}
              onPress={() => {
                const randomIndex = Math.floor(Math.random() * previewExamples.length);
                setPreviewText(previewExamples[randomIndex]);
              }}
            >
              <Feather name="refresh-cw" size={16} color={colors.primary} />
              <Text style={styles.refreshPreviewText}>New example</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Accessibility Info */}
        <View style={styles.infoSection}>
          <View style={styles.infoIcon}>
            <Feather name="info" size={18} color={colors.primary} />
          </View>
          <View style={styles.infoContent}>
            <Text style={styles.infoTitle}>Accessibility Tip</Text>
            <Text style={styles.infoText}>
              Larger font sizes improve readability for users with visual impairments. 
              Combined with bold text, it can significantly enhance the reading experience.
            </Text>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  header: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 20,
    paddingTop: 10,
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
  },
  resetButton: {
    padding: 8,
  },
  resetButtonText: {
    fontSize: 16,
    color: '#4F46E5',
    fontWeight: '500',
  },
  headerSubtitle: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    paddingBottom: 30,
  },
  currentSizeSection: {
    paddingHorizontal: 20,
    marginTop: 20,
    marginBottom: 24,
  },
  currentSizeCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  currentSizeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  currentSizeLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginLeft: 12,
  },
  currentSizeValue: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  currentSizeNumber: {
    fontSize: 32,
    fontWeight: '700',
    color: '#4F46E5',
  },
  currentSizeBadge: {
    backgroundColor: '#4F46E510',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
  },
  currentSizeBadgeText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#4F46E5',
  },
  sliderSection: {
    backgroundColor: '#FFFFFF',
    marginHorizontal: 20,
    marginBottom: 24,
    padding: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  sliderHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  sliderValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#4F46E5',
  },
  slider: {
    width: '100%',
    height: 40,
  },
  sliderLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
    marginBottom: 4,
  },
  sliderLabel: {
    color: '#6B7280',
  },
  sliderSizeLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  sliderSizeLabel: {
    fontSize: 12,
    color: '#9CA3AF',
  },
  presetsSection: {
    paddingHorizontal: 20,
    marginBottom: 24,
  },
  presetsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginTop: 12,
  },
  presetCard: {
    width: '48%',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  presetCardActive: {
    borderColor: '#4F46E5',
    backgroundColor: '#4F46E505',
  },
  presetIcon: {
    marginBottom: 12,
    height: 40,
    justifyContent: 'center',
  },
  presetName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
    textAlign: 'center',
  },
  presetNameActive: {
    color: '#4F46E5',
  },
  presetDescription: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
  },
  recommendedBadge: {
    position: 'absolute',
    top: -6,
    backgroundColor: '#10B981',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  recommendedBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  activeIndicator: {
    position: 'absolute',
    top: 8,
    right: 8,
  },
  boldSection: {
    paddingHorizontal: 20,
    marginBottom: 24,
  },
  boldCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  boldLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  boldIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  boldText: {
    flex: 1,
  },
  boldTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  boldDescription: {
    fontSize: 14,
    color: '#6B7280',
  },
  boldToggle: {
    width: 52,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#E5E7EB',
    padding: 2,
  },
  boldToggleActive: {
    backgroundColor: '#4F46E5',
  },
  boldToggleCircle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 1,
    elevation: 2,
  },
  boldToggleCircleActive: {
    transform: [{ translateX: 24 }],
  },
  previewSection: {
    paddingHorizontal: 20,
    marginBottom: 24,
  },
  previewCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    marginTop: 12,
  },
  previewText: {
    color: '#1F2937',
    marginBottom: 16,
  },
  refreshPreview: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
  },
  refreshPreviewText: {
    fontSize: 14,
    color: '#4F46E5',
    fontWeight: '500',
    marginLeft: 8,
  },
  infoSection: {
    flexDirection: 'row',
    backgroundColor: '#4F46E505',
    marginHorizontal: 20,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#4F46E520',
  },
  infoIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#4F46E510',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  infoContent: {
    flex: 1,
  },
  infoTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  infoText: {
    fontSize: 13,
    color: '#6B7280',
    lineHeight: 18,
  },
});

export default FontSizeSettings;